import React from 'react';
import ConfirmContent from './ConfirmContent';
import * as ticketStatuses from './../../../constants/ticketStatuses';
import { isLiveChat } from 'util/chat';
import { createPopup, closePopup } from '../../../services/popupService';

// Functions to configure the react-popup ConfirmPopup component
// to modify button styles change the btnConfig classNames
// to modify body content supply a different component inplace of ConfirmContent
export const twoBtnPopupCreator = ({ content, btnConfig, onConfirm, onDismiss }) =>
  createPopup({
    type: 'ConfirmContent',
    title: null,
    content: <ConfirmContent {...content} />,
    noOverlay: false,
    buttons: {
      right: [
        {
          text: btnConfig.dismiss.text || btnConfig.dismiss,
          className: btnConfig.dismiss.className || 'dismiss',
          action: () => {
            if (onDismiss) {
              onDismiss();
            }
            closePopup();
          },
        },
        {
          text: btnConfig.confirm.text || btnConfig.confirm,
          className: btnConfig.dismiss.className || 'confirm',
          action: () => {
            onConfirm();
            closePopup();
          },
        },
      ],
    },
  });

export const oneBtnPopupCreator = ({ content, btnConfig, onBtnClick }) =>
  createPopup({
    type: 'ConfirmContent',
    title: null,
    content: <ConfirmContent {...content} />,
    noOverlay: false,
    buttons: {
      right: [
        {
          text: btnConfig.text || btnConfig,
          className: btnConfig.className || 'one-btn-dismiss',
          action: () => {
            if (onBtnClick) {
              onBtnClick();
            }
            closePopup();
          },
        },
      ],
    },
  });

export const openConfirmOutOfScopePopup = ({ content, btnConfig, onConfirm, onDismiss }) =>
  createPopup({
    type: 'ConfirmContent',
    title: null,
    content: <ConfirmContent {...content} />,
    noOverlay: false,
    closeOnOutsideClick: false,
    buttons: {
      right: [
        {
          text: btnConfig.dismiss.text || btnConfig.dismiss,
          className: btnConfig.dismiss.className || 'dismiss',
          action: () => {
            if (onDismiss) {
              onDismiss();
            }
            closePopup();
          },
        },
        {
          text: btnConfig.confirm.text || btnConfig.confirm,
          className: btnConfig.dismiss.className || 'confirm',
          action: () => {
            onConfirm();
            closePopup();
          },
        },
      ],
    },
  });

// Frequently used button config constants for escalation popup

export const escalateToSpecialistContent = {
  main: 'Are you sure you want to escalate?',
  subText: ["If you can't resolve the ticket on your own, escalate to a specialist"],
  iconPath: '/images/green-alert-bell.svg',
};

export const escalateToSmbContent = {
  main: 'Are you sure you want to escalate?',
  subText: ["If you can't resolve the ticket on your own, escalate to a partner"],
  iconPath: '/images/green-alert-bell.svg',
};

export const escalateConfirmConfig = {
  content: {
    main: 'This ticket has been escalated!',
    subText: ["Sorry you couldn't resolve this case.", 'No need to take any other actions. Thanks!'],
    iconPath: '/images/checkmark-green.svg',
  },
  btnConfig: 'CLOSE',
};

export const escalatePopupLabels = {
  allPopupLabel: 'allPopup',
  assignedPopupLabel: 'assignedPopup',
  escalatedPopupLabel: 'escalatedPopup',
};

export const createPromoteUserPopupConfig = (expertName) => ({
  content: {
    main: `${expertName} has been promoted!`,
    iconPath: '/images/checkmark-green.svg',
  },
  btnConfig: 'DISMISS',
});

export const ESCALATE_POPUP_CONFIG = {
  escalateToSpecialistContent,
  escalateToSmbContent,
  escalateConfirmConfig,
  escalatePopupLabels,
};

export const CANNOT_HANDLE_POPUP_CONFIG = {
  content: {
    main: "Simplr can't handle this ticket",
    subText: ["You selected an option that Simplr doesn't handle. "],
    iconPath: '/images/green-alert-bell.svg',
  },
  btnConfig: 'CLOSE',
};

export const macroNotCopiedConfig = {
  content: {
    main: 'The selected text was not copied to your text area',
    subText: ['Please select a different response'],
    iconPath: '/images/green-alert-bell.svg',
  },
  btnConfig: 'CLOSE',
};

export const ticketCategoryDismissConfig = {
  content: {
    main: 'This ticket was removed from your queue.',
    subText: ['This ticket category cannot be taken', 'Please try another ticket.'],
    iconPath: '/images/checkmark-green.svg',
  },
  btnConfig: 'CLOSE',
};

export const simplrTagsUpdatedConfig = {
  content: {
    main: 'Simplr tags updated',
    subText: ['The simplr tags for this partner have been updated'],
    iconPath: '/images/checkmark-green.svg',
  },
  btnConfig: 'CLOSE',
};

export const partnerInfoUpdatedConfig = {
  content: {
    main: 'Partner info updated',
    subText: ['The record for this partner has been updated'],
    iconPath: '/images/checkmark-green.svg',
  },
  btnConfig: 'CLOSE',
};

export const decisionTreesUpdatedConfig = {
  content: {
    main: 'Decision trees updated',
    subText: ['This partners decision trees have been updated'],
    iconPath: '/images/checkmark-green.svg',
  },
  btnConfig: 'CLOSE',
};

export const decisionTreesDraftsUpdatedConfig = {
  content: {
    main: 'Decision tree drafts updated',
    subText: ["This partner's decision trees drafts have been updated"],
    iconPath: '/images/checkmark-green.svg',
  },
  btnConfig: 'CLOSE',
};

export const decisionTreesDraftsDeletedConfig = {
  content: {
    main: 'Draft Deleted',
    subText: ['The draft tree has been deleted'],
    iconPath: '/images/checkmark-green.svg',
  },
  btnConfig: 'CLOSE',
};

export const decisionTreeTemplateDeletedConfig = {
  content: {
    main: 'Draft Deleted',
    subText: ['The template tree has been deleted'],
    iconPath: '/images/checkmark-green.svg',
  },
  btnConfig: 'CLOSE',
};

export const decisionTreeTemplateAddedConfig = {
  content: {
    main: 'Decision tree template added',
    subText: ['This tree has been added as a template'],
    iconPath: '/images/checkmark-green.svg',
  },
  btnConfig: 'CLOSE',
};

export const createDiscountPeriodPopup = (actionType) => {
  const content = {
    iconPath: '/images/checkmark-green.svg',
  };
  if (actionType === 'NEW') {
    content.main = 'Period Created';
    content.subText = ['The discount period has been created'];
  } else if (actionType === 'UPDATE') {
    content.main = 'Period Updated';
    content.subText = ['The discount period has been updated'];
  } else {
    content.main = 'Period Deleted';
    content.subText = ['The discount period has been deleted'];
  }

  return {
    content,
    btnConfig: 'CLOSE',
  };
};

export const ticketCategoryDismissAndHideConfig = {
  content: {
    main: 'This ticket was removed from your queue and hidden.',
    subText: ['This ticket category cannot be taken', 'Please try another ticket.'],
    iconPath: '/images/checkmark-green.svg',
  },
  btnConfig: 'CLOSE',
};

export const getUnassignmentPopupConfig = (conversationObj, doesCurrentExpertHaveLastReply) => {
  const status = conversationObj.status;
  const ticketNumber = conversationObj.simplrTicketNumber;
  const communicationType = conversationObj.communicationType;

  const CUSTOMER_LEFT_MESSAGE = `${ticketNumber} Customer has left the chat`;
  const EXPERT_STILL_PAID_MESSAGE = "You'll still be paid.";

  let subText = [];

  switch (status) {
    case ticketStatuses.TICKET_STATUS_TAKEN_BY_SMB:
      subText.push(`Ticket ${ticketNumber} was taken by the partner.`, 'Please try another ticket.');
      break;
    case ticketStatuses.TICKET_STATUS_ASSIGNED:
      subText.push(`Ticket ${ticketNumber} was removed from your queue.`, 'Please try another ticket.');
      break;
    case ticketStatuses.TICKET_STATUS_TIMEOUT_CONVERSATION_CLOSED:
      if (isLiveChat(communicationType)) {
        subText.push(CUSTOMER_LEFT_MESSAGE);
      } else {
        subText.push(`Ticket ${ticketNumber} was auto-resolved as the customer hasn't responded in 48 hrs.`);
      }

      if (doesCurrentExpertHaveLastReply) {
        subText.push(EXPERT_STILL_PAID_MESSAGE);
      }

      break;
    case ticketStatuses.TICKET_STATUS_CLOSED:
      if (isLiveChat(communicationType)) {
        subText.push(CUSTOMER_LEFT_MESSAGE);
      } else {
        subText.push(`Ticket ${ticketNumber} was closed by the partner.`);
      }

      if (doesCurrentExpertHaveLastReply) {
        subText.push(EXPERT_STILL_PAID_MESSAGE);
      }

      break;

    case ticketStatuses.TICKET_STATUS_SELF_HELP:
      subText.push('The ticket has been transferred to the bot.');
      subText.push(EXPERT_STILL_PAID_MESSAGE);

      break;

    case ticketStatuses.TICKET_STATUS_DELETED:
      subText.push(`Ticket ${ticketNumber} was deleted by the partner.`);
      break;

    default:
      return;
  }

  return {
    content: {
      main: 'This ticket was removed from your queue.',
      subText,
      iconPath: '/images/checkmark-green.svg',
    },
    btnConfig: 'CLOSE',
  };
};

export const couponCodeUploadConfirmConfig = {
  content: {
    main: 'Coupon Codes Uploaded!',
    subText: ['Coupon codes successfully uploaded.'],
    iconPath: '/images/checkmark-green.svg',
  },
  btnConfig: 'CLOSE',
};
